import type { LandingEditorialContent } from './types';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export { MONTHS };

export function nameToSlug(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, '-');
}

export function formatPrice(
  amount: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (amount == null || amount <= 0) {
    return '—';
  }

  const code = currency?.trim() || 'USD';
  return `${code} ${Math.round(amount).toLocaleString()}`;
}

export function formatPriceRange(
  minPrice: number | null | undefined,
  maxPrice: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (minPrice == null || minPrice <= 0) {
    return 'Contact for pricing';
  }

  const min = formatPrice(minPrice, currency);
  if (maxPrice == null || maxPrice <= minPrice) {
    return `From ${min}`;
  }

  return `${min} – ${formatPrice(maxPrice, currency)}`;
}

export function formatDepartureDate(
  isoDate: string | null | undefined,
): string {
  if (!isoDate) {
    return '—';
  }

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function buildGenericCountryEditorial(
  countryName: string,
): LandingEditorialContent {
  return {
    overview: `Compare liveaboard diving trips in ${countryName}. Browse upcoming departures, compare boats and itineraries, and book with flexible payment options.`,
    bestSeason: 'Varies by route — see seasonality below',
    duration: 'Typically 4–7 nights',
    bestFor: 'Open-water certified divers and above',
    highlights: [
      `Upcoming liveaboard departures across ${countryName}`,
      'Compare boats, cabins, and trip prices in one place',
      'Book now, pay later on select trips',
    ],
    faqs: [
      {
        question: `When is the best time to dive in ${countryName}?`,
        answer:
          'Seasons vary by region and route. Check the seasonality table and individual route pages for the best windows at each destination.',
      },
      {
        question: `How do I book a liveaboard in ${countryName}?`,
        answer:
          'Browse available trips below, select your preferred boat and dates, then complete your booking online. Santtai offers flexible payment on select departures.',
      },
    ],
    lastUpdated: '2026-07-04',
  };
}

export function buildGenericRouteEditorial(
  routeName: string,
  countryName?: string | null,
): LandingEditorialContent {
  const location = countryName ? `${routeName}, ${countryName}` : routeName;

  return {
    overview: `Explore liveaboard diving trips to ${location}. Compare boats, check upcoming departures, and find the best price for your next dive adventure.`,
    bestSeason: 'Varies — check departures below',
    duration: 'Typically 4–7 nights',
    bestFor: 'Certified divers seeking guided liveaboard trips',
    highlights: [
      `Liveaboard trips to ${routeName}`,
      'Compare boats, prices, and upcoming departures',
      'Flexible booking options on select trips',
    ],
    faqs: [
      {
        question: `What boats operate on the ${routeName} route?`,
        answer:
          'See the boat grid below for operators with upcoming departures on this route, including starting prices where available.',
      },
      {
        question: `How do I book a ${routeName} liveaboard?`,
        answer:
          'Choose a boat and departure from the listings below, then follow the booking flow. You can also contact Santtai for help choosing the right trip.',
      },
    ],
    lastUpdated: '2026-07-04',
  };
}

const COUNTRY_CONTENT: Record<string, LandingEditorialContent> = {
  thailand: {
    overview:
      'From the whale sharks of Richelieu Rock to the coral gardens of the Similan Islands, Thailand offers world-class liveaboard diving on both the Andaman Sea and Gulf coasts. Compare routes, boats, and seasons to plan your trip.',
    bestSeason: 'Nov – Apr (Andaman); Mar – Aug (Gulf)',
    duration: '4–7 nights typical',
    bestFor: 'All levels — routes from beginner-friendly to advanced',
    highlights: [
      'Whale shark and manta encounters at Richelieu Rock and Koh Bon',
      'Similan Islands granite boulders and pristine reefs',
      'Year-round diving across Andaman and Gulf routes',
      'Warm water (27–30°C) and excellent visibility in peak season',
    ],
    seasonality: {
      description:
        'The Andaman Sea and Gulf of Thailand have different seasons. Plan around the route you want to dive.',
      rows: [
        {
          label: 'North Andaman',
          months: [
            'excellent',
            'excellent',
            'excellent',
            'good',
            'poor',
            'poor',
            'poor',
            'poor',
            'poor',
            'fair',
            'good',
            'excellent',
          ],
        },
        {
          label: 'South Andaman',
          months: [
            'poor',
            'poor',
            'poor',
            'fair',
            'good',
            'excellent',
            'excellent',
            'excellent',
            'good',
            'fair',
            'poor',
            'poor',
          ],
        },
        {
          label: 'Koh Tao (Gulf)',
          months: [
            'good',
            'good',
            'excellent',
            'excellent',
            'excellent',
            'good',
            'good',
            'good',
            'fair',
            'poor',
            'poor',
            'fair',
          ],
        },
      ],
      tip: 'Thailand has diving year-round if you know where to look. North Andaman (Nov–Apr) and South Andaman (late Apr–Oct) run back-to-back, with Gulf routes filling the gaps.',
    },
    faqs: [
      {
        question: 'When is the best time for liveaboard diving in Thailand?',
        answer:
          'The Andaman Sea peak season is November through April. The Gulf coast (Koh Tao) is best March through August. South Andaman runs late April through October.',
      },
      {
        question: 'Do I need an Advanced Open Water certification?',
        answer:
          'Most Thailand liveaboards accept Open Water divers, though some deep sites suit Advanced certification. Check individual trip requirements before booking.',
      },
      {
        question: 'Where do Thailand liveaboards depart from?',
        answer:
          'Common departure ports include Khao Lak and Phuket for Andaman routes, and Koh Tao or Chumphon for Gulf trips.',
      },
    ],
    relatedLinks: [
      { label: 'Thailand diving guide', href: '/guides/thailand' },
    ],
    lastUpdated: '2026-07-04',
  },
  indonesia: {
    overview:
      'Indonesia spans the Coral Triangle with iconic liveaboard destinations including Raja Ampat, Komodo, and the Banda Sea. Compare routes and boats to find your next expedition.',
    bestSeason: 'Varies — Oct–Apr (Raja Ampat); Apr–Nov (Komodo)',
    duration: '7–10 nights typical',
    bestFor: 'Experienced divers; some routes suit all levels',
    highlights: [
      'Raja Ampat biodiversity and pristine reefs',
      'Komodo dragons and drift diving',
      'Remote Banda Sea and Alor expeditions',
    ],
    faqs: [
      {
        question: 'When should I visit Raja Ampat vs Komodo?',
        answer:
          'Raja Ampat is generally best October through April. Komodo season runs roughly April through November. Check route-specific departures below.',
      },
    ],
    lastUpdated: '2026-07-04',
  },
  maldives: {
    overview:
      'The Maldives offers classic atoll-hopping liveaboards with manta cleaning stations, channel dives, and pristine reefs. Compare central, northern, and southern atoll routes.',
    bestSeason: 'Nov – Apr (dry season)',
    duration: '7 nights typical',
    bestFor: 'All levels; strong currents on some sites',
    highlights: [
      'Manta ray and whale shark encounters',
      'Channel dives with pelagic action',
      'Luxury and mid-range liveaboard options',
    ],
    faqs: [
      {
        question: 'What is the best season for Maldives liveaboards?',
        answer:
          'The dry northeast monsoon (November–April) generally offers the best visibility and calmest seas, though diving is available year-round.',
      },
    ],
    lastUpdated: '2026-07-04',
  },
};

const ROUTE_CONTENT: Record<string, LandingEditorialContent> = {
  'north-andaman': {
    overview:
      "The North Andaman route is Thailand's crown jewel for liveaboard diving — Similan Islands, Koh Bon, Koh Tachai, and Richelieu Rock. Expect whale sharks in season, manta rays at cleaning stations, and world-class reef scenery.",
    bestSeason: 'October – May',
    duration: '4–5 nights typical',
    bestFor: 'All levels; some current on exposed sites',
    highlights: [
      'Richelieu Rock — top site for whale shark encounters',
      'Similan Islands granite boulders and reef fish',
      'Manta rays at Koh Bon cleaning stations',
      'Crystal-clear water in peak season',
    ],
    faqs: [
      {
        question: 'What are the must-dive sites on North Andaman?',
        answer:
          'Richelieu Rock, Similan Islands, Koh Bon, and Koh Tachai are the signature sites on this route.',
      },
      {
        question: 'When is whale shark season at Richelieu Rock?',
        answer:
          'February through April offers the best chances, though encounters are possible throughout the season.',
      },
    ],
    lastUpdated: '2026-07-04',
  },
  'south-andaman': {
    overview:
      'The South Andaman route covers dramatic deep-water pinnacles including Hin Daeng and Hin Muang, plus Koh Haa and sites around Phi Phi. Known for manta rays, soft coral walls, and varied topography.',
    bestSeason: 'Late April – October',
    duration: '4–5 nights typical',
    bestFor: 'Intermediate divers and above',
    highlights: [
      'Hin Daeng and Hin Muang pinnacle dives',
      'Regular manta ray visits',
      'Koh Haa lagoon and cavern sites',
    ],
    faqs: [
      {
        question: 'How does South Andaman differ from North Andaman?',
        answer:
          'South Andaman runs in the opposite season, focusing on southern pinnacles and deeper sites. It complements the North Andaman calendar for year-round Thailand diving.',
      },
    ],
    lastUpdated: '2026-07-04',
  },
  komodo: {
    overview:
      'Komodo liveaboards combine world-class diving with encounters with Komodo dragons. Expect drift dives, mantas at cleaning stations, and vibrant coral reefs in Komodo National Park.',
    bestSeason: 'April – November',
    duration: '7 nights typical',
    bestFor: 'Intermediate+; drift diving experience helpful',
    highlights: [
      'Manta ray cleaning stations',
      'Komodo National Park biodiversity',
      'Dramatic drift dives and coral gardens',
    ],
    faqs: [
      {
        question: 'Is Komodo suitable for beginner divers?',
        answer:
          'Some sites are beginner-friendly, but many dives involve current. Open Water divers should choose itineraries suited to their experience.',
      },
    ],
    lastUpdated: '2026-07-04',
  },
  'raja-ampat': {
    overview:
      'Raja Ampat is the heart of the Coral Triangle — unmatched reef biodiversity, endemic species, and pristine hard and soft coral gardens across hundreds of islands.',
    bestSeason: 'October – April',
    duration: '7–10 nights typical',
    bestFor: 'All levels; photography and macro enthusiasts',
    highlights: [
      'Highest marine biodiversity on Earth',
      'Manta rays and reef sharks',
      'World-class macro and wide-angle photography',
    ],
    faqs: [
      {
        question: 'How long should a Raja Ampat liveaboard be?',
        answer:
          'Most divers choose 7–10 nights to cover the central and northern highlights. Longer trips reach more remote sites.',
      },
    ],
    lastUpdated: '2026-07-04',
  },
};

export function getCountryEditorial(
  slug: string,
  countryName: string,
): LandingEditorialContent {
  return (
    COUNTRY_CONTENT[slug.toLowerCase()] ??
    buildGenericCountryEditorial(countryName)
  );
}

export function getRouteEditorial(
  slug: string,
  routeName: string,
  countryName?: string | null,
): LandingEditorialContent {
  return (
    ROUTE_CONTENT[slug.toLowerCase()] ??
    buildGenericRouteEditorial(routeName, countryName)
  );
}
